import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  NATIVE_CHECKOUT_PATH,
  customCheckoutAvailable,
  decideCheckoutNavigation,
  resolveCheckoutTarget,
  listRecipients,
  buildStorefrontCartInput,
} from '@theme/checkout-fallback';

/**
 * Storefront API cartCreate -- the client-side, backend-free checkout-prefill
 * path (see #createStorefrontCheckout). Requests only what onCheckoutClick
 * needs: the id (unused, but cheap) and the checkoutUrl to redirect to.
 */
const STOREFRONT_CART_CREATE_MUTATION = `
  mutation cartCreateWithDelivery($input: CartInput!) {
    cartCreate(input: $input) {
      cart { id checkoutUrl }
      userErrors { field message }
    }
  }
`;

/** Storefront API calls are first-party and normally fast; bound the new
 * stage so a hang can't stall the (still-unbounded) web-raku fallback stage
 * from getting its turn. */
const STOREFRONT_REQUEST_TIMEOUT_MS = 4000;

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove (native bundles, plus addon/card
      // lines linked to this product line via a shared `id` cart line property)
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    const keysToRemove = rowsToRemove.map((row) => row.dataset.key).filter(Boolean);

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
      keys: keysToRemove.length > 1 ? keysToRemove : undefined,
    });

    this.#scrollToTop();

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Returns the shopper to the top of the cart page after removing a line item.
   *
   * Removing an item reflows everything below it, so someone deleting from
   * halfway down a long cart is left looking at a different set of rows than the
   * one they acted on. Scrolling back to the top re-anchors them on the cart as a
   * whole — and on the empty state, when that was the last line.
   *
   * Drawer carts are deliberately excluded: the page behind the drawer is not
   * what changed, and scrolling it would move content the shopper cannot see.
   */
  #scrollToTop() {
    if (this.isDrawer) return;

    window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {string[]} [config.keys] - When provided, removes all of these line item keys at
   * once instead of just `line` (used to cascade-delete addon/card lines sharing a `properties.id`
   * with the product line being removed).
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity, keys } = config;
    const isBatch = Array.isArray(keys) && keys.length > 0;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = isBatch
      ? JSON.stringify({
          updates: Object.fromEntries(keys.map((key) => [key, 0])),
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        })
      : JSON.stringify({
          line: line,
          quantity: quantity,
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        });

    cartTotal?.shimmer();

    fetch(isBatch ? Theme.routes.cart_update_url : Theme.routes.cart_change_url, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        if (parsedResponseText.errors) {
          if (isBatch) {
            console.error(parsedResponseText.errors);
          } else {
            this.#handleCartError(line, parsedResponseText);
          }
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // resetShimmer used to live inside the success branch above, so any
        // throw before it left the cart total shimmering forever with no error
        // and no way to retry. JSON.parse is the usual culprit: Shopify answers
        // with HTML rather than JSON on 429/430 rate limiting, a Cloudflare
        // interstitial or an expired session -- exactly what a promo spike
        // produces. Reset it here so the total always settles.
        resetShimmer(this);
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles a click on the cart's "Check out" button.
   *
   * In plain terms: normally we hand off to Shopify's own checkout. For gift
   * orders that carry a delivery city, an optional app service ("create checkout")
   * builds a checkout with the address pre-filled and gives us a URL to send the
   * shopper to. If that service isn't set up, is slow, fails, or returns anything
   * we don't trust, we fall back to the normal Shopify checkout instead. This
   * function must NEVER send the shopper to a broken URL — that was the old bug
   * (it navigated to `/undefined`). See assets/checkout-fallback.js for the rules.
   *
   * Technical: reads the shipping-address line-item properties off the cart line
   * with `properties.type === 'product'`; if a delivery city is present, asks the
   * backend to create a Storefront Cart with that address as a delivery preference
   * and redirects to the returned checkout URL. Checkout Extensibility strips
   * `checkout[shipping_address][...]` URL params before checkout loads, so the
   * prefill has to happen server-side.
   * @param {MouseEvent} event - The checkout button click event.
   */
  async onCheckoutClick(event) {
    // Must prevent the native form submit synchronously - doing it after the
    // `await` below would be too late, since the browser will have already
    // submitted the form by the time this resumes.
    event.preventDefault();

    const theme = typeof Theme !== 'undefined' ? Theme : undefined;

    // Locate the real checkout button. Horizon's `on:click` fires from a
    // document-level listener, so `event.target` (not `currentTarget`) is the
    // element carrying `on:click`; fall back to querying the button directly.
    // The button's `name="checkout"` is what makes the native form submit route
    // to Shopify checkout rather than a cart update, so we must submit *with* it.
    const checkoutButton = /** @type {HTMLButtonElement | null} */ (
      (event.target instanceof Element && event.target.closest('button[name="checkout"]')) ||
      this.querySelector('button[name="checkout"]') ||
      document.querySelector('button[name="checkout"][form="cart-form"]')
    );

    // Guaranteed-safe fallback: submit the cart form with the checkout button so
    // Shopify routes to /checkout exactly as it would with no custom handler.
    // Never navigates to an undefined/relative URL (the `/undefined` 404 bug).
    const goToNativeCheckout = () => {
      const form = checkoutButton?.form ?? document.getElementById('cart-form');
      if (form instanceof HTMLFormElement && checkoutButton && typeof form.requestSubmit === 'function') {
        form.requestSubmit(checkoutButton);
      } else {
        window.location.assign(NATIVE_CHECKOUT_PATH);
      }
    };

    // Client-side Storefront API cartCreate (primary) and the app-provided
    // web-raku checkout-create service (fallback) are two independent prefill
    // mechanisms; if neither is configured, don't touch the cart or
    // dereference undefined globals — go straight to native checkout.
    // Outerbloom checkout authenticates through BE Raku's dedicated custom app.
    // Ignore any legacy public token still saved in this store's theme settings.
    const hasStorefront = theme?.shop?.domain !== 'outerbloom1.myshopify.com' && Boolean(theme?.storefrontApi?.token);
    const hasWebRaku = customCheckoutAvailable(theme);

    if (!hasStorefront && !hasWebRaku) {
      goToNativeCheckout();
      return;
    }

    let cart;

    try {
      const response = await fetch(`${theme.routes.cart_url}.js`);
      cart = await response.json();
    } catch (error) {
      console.error(error);
      goToNativeCheckout();
      return;
    }

    // A single Shopify checkout carries one shipping address, so when the cart
    // holds gifts for more than one recipient we ask which recipient to pre-fill
    // and send that choice to whichever prefill stage below ends up firing.
    // Nothing is persisted: every checkout click re-prompts, so returning to
    // the cart and picking again always wins. Cancelling the dialog keeps the
    // shopper on the cart.
    let selectedRecipientId = '';
    const recipients = listRecipients(cart);
    if (recipients.length > 1) {
      selectedRecipientId = await this.#pickRecipient(recipients);
      if (!selectedRecipientId) return;
    }

    if (hasStorefront) {
      const storefrontUrl = await this.#createStorefrontCheckout(theme, cart, selectedRecipientId);
      if (storefrontUrl) {
        const target = resolveCheckoutTarget(storefrontUrl, [theme.shop.domain, theme.shop.primaryDomain]);
        if (target !== NATIVE_CHECKOUT_PATH) {
          window.location.assign(target);
          return;
        }
      }
    }

    if (hasWebRaku) {
      const decision = decideCheckoutNavigation(theme, cart, selectedRecipientId);
      if (decision.mode === 'custom-create') {
        try {
          const checkoutResponse = await fetch(
            `${decision.createUrl}?domain=${encodeURIComponent(decision.shopDomain)}`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify([
                cart,
                { note: cart.note },
                { customer: { email: decision.customerEmail } },
                { selectedRecipientId },
              ]),
            }
          );
          if (!checkoutResponse.ok) throw new Error(`checkout create failed: ${checkoutResponse.status}`);
          const checkoutData = await checkoutResponse.json();
          const target = resolveCheckoutTarget(checkoutData?.checkout?.webUrl, [decision.shopDomain, theme.shop.primaryDomain]);

          if (target !== NATIVE_CHECKOUT_PATH) {
            window.location.assign(target);
            return;
          }
        } catch (error) {
          console.error(error);
        }
      }
    }

    goToNativeCheckout();
  }

  /**
   * Client-side Storefront API cartCreate: builds a Storefront cart with the
   * selected recipient's delivery address baked in, and returns its
   * checkoutUrl. This is the primary, backend-free replacement for the
   * web-raku checkout-create service. Never throws -- returns null on any
   * problem (network error, non-2xx, GraphQL errors, non-empty userErrors, or
   * a missing checkoutUrl) so the caller can fall through to the next stage.
   *
   * @param {any} theme
   * @param {any} cart
   * @param {string} selectedRecipientId
   * @returns {Promise<string | null>}
   */
  async #createStorefrontCheckout(theme, cart, selectedRecipientId) {
    const input = buildStorefrontCartInput(theme, cart, selectedRecipientId);
    if (!input) return null;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), STOREFRONT_REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(`https://${theme.shop.domain}/api/${theme.storefrontApi.version}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': theme.storefrontApi.token,
        },
        body: JSON.stringify({ query: STOREFRONT_CART_CREATE_MUTATION, variables: { input } }),
        signal: controller.signal,
      });
      if (!response.ok) return null;

      const data = await response.json();
      const cartCreate = data?.data?.cartCreate;
      if (!cartCreate || (cartCreate.userErrors && cartCreate.userErrors.length > 0)) {
        if (cartCreate?.userErrors?.length) console.error('Storefront cartCreate userErrors:', cartCreate.userErrors);
        return null;
      }

      return cartCreate.cart?.checkoutUrl ?? null;
    } catch (error) {
      console.error(error);
      return null;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Ask the shopper which recipient's address to pre-fill at checkout. Shows a
   * modal listing each recipient (name + address + city) and resolves with the
   * chosen recipient `id`, or '' if the shopper cancels or dismisses it.
   *
   * Uses a native <dialog>: showModal gives a focus trap, Escape handling, and
   * an inert background for free. Recipient text is set via textContent — never
   * innerHTML — because names and addresses are shopper-supplied.
   *
   * @param {Array<{ id: string, name: string, address: string, city: string }>} recipients
   * @returns {Promise<string>} The chosen recipient id, or '' when dismissed.
   */
  #pickRecipient(recipients) {
    return new Promise((resolve) => {
      const dialog = document.createElement('dialog');
      dialog.className = 'recipient-picker';
      dialog.setAttribute('aria-label', 'Pilih alamat penerima');

      const content = document.createElement('div');
      content.className = 'recipient-picker__content';

      const title = document.createElement('h2');
      title.className = 'recipient-picker__title';
      title.textContent = 'Pilih alamat penerima';
      content.appendChild(title);

      const desc = document.createElement('p');
      desc.className = 'recipient-picker__desc';
      desc.textContent =
        'Keranjang berisi beberapa penerima. Pilih satu alamat yang akan diisi di halaman checkout.';
      content.appendChild(desc);

      let done = false;
      /** @param {string} id */
      const finish = (id) => {
        if (done) return;
        done = true;
        dialog.close();
        dialog.remove();
        resolve(id);
      };

      const list = document.createElement('div');
      list.className = 'recipient-picker__list';
      for (const recipient of recipients) {
        const option = document.createElement('button');
        option.type = 'button';
        option.className = 'recipient-picker__option';

        const name = document.createElement('span');
        name.className = 'recipient-picker__name';
        name.textContent = recipient.name || 'Penerima';
        option.appendChild(name);

        const address = document.createElement('span');
        address.className = 'recipient-picker__address';
        address.textContent = [recipient.address, recipient.city].filter(Boolean).join(', ');
        option.appendChild(address);

        option.addEventListener('click', () => finish(recipient.id));
        list.appendChild(option);
      }
      content.appendChild(list);

      const cancel = document.createElement('button');
      cancel.type = 'button';
      cancel.className = 'recipient-picker__cancel';
      cancel.textContent = 'Batal';
      cancel.addEventListener('click', () => finish(''));
      content.appendChild(cancel);

      dialog.appendChild(content);

      // Escape key surfaces as the dialog's `cancel` event; treat as no selection.
      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        finish('');
      });
      // Backdrop click: the event target is the dialog itself, not inner content.
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) finish('');
      });

      document.body.appendChild(dialog);
      dialog.showModal();
    });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
